import { RouteObject, Navigate } from 'react-router-dom'
import { getRouteComponent } from './routeRegistry'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { AdminLayout } from '@/components/layout/AdminLayout'
interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

interface GenerateRoutesOptions {
  includeFallback?: boolean
}

/**
 * Generate React Router routes from user_routes (database navigation)
 */
export function generateRoutesFromUserRoutes(
  userRoutes: UserRoute[],
  publicRoutes: RouteObject[] = [],
  options: GenerateRoutesOptions = {}
): RouteObject[] {
  const routes: RouteObject[] = [
    // Public routes (always available)
    ...publicRoutes,
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
  ]

  // Client never show sidebar
  
  const isClientRoute = (path: string) => path.startsWith('/client')

  // Wrap pages: client = AuthGuard only, admin/helpdesk = AuthGuard + AdminLayout (sidebar)
  const wrapProtected = (element: JSX.Element, path: string) => {
    

  return (
    <AuthGuard>
      <AdminLayout>{element}</AdminLayout>
    </AuthGuard>
  )
  }

  /**
   * Recursively process routes and their children
   */
  function processRoute(route: UserRoute, parentPath: string = '') {
    let fullPath: string

    if (route.path.startsWith('/')) {
      // Absolute path - check if it exists in route registry
      // If not, try joining with parentPath (for cases like /users under /user-management)
      const absolutePath = route.path
      const ComponentForAbsolute = getRouteComponent(absolutePath)

      if (ComponentForAbsolute) {
        // Absolute path exists in registry (e.g., /shipping-lines/create)
        fullPath = absolutePath
      } else if (parentPath) {
        // Absolute path doesn't exist, try joining with parent
        // (e.g. backend sends child path "/service-types" under "/service-catalog" -> /service-catalog/service-types)
        const joined =
          parentPath.replace(/\/+$/, '') + '/' + absolutePath.replace(/^\/+/, '')
        const joinedPath = joined.startsWith('/') ? joined : '/' + joined
        const ComponentForJoined = getRouteComponent(joinedPath)

        if (ComponentForJoined) {
          fullPath = joinedPath
        } else {
          // Neither absolute nor joined path exists, use absolute as fallback
          fullPath = absolutePath
        }
      } else {
        // No parent path, use absolute as-is
        fullPath = absolutePath
      }
    } else {
      // Relative path - join with parentPath (use slash like Sidebar joinPaths)
      const joined = parentPath
        ? `${parentPath.replace(/\/+$/, '')}/${route.path.replace(/^\/+/, '')}`
        : route.path
      fullPath = joined.startsWith('/') ? joined : `/${joined}`
    }

    // Get component for this route (registry keys use leading slash)
    const Component = getRouteComponent(fullPath)
    const alreadyRegistered = routes.some((r) => r.path === fullPath)

    if (Component && !alreadyRegistered) {
      routes.push({
        path: fullPath,
        element: wrapProtected(<Component />, fullPath),
      })
    }

    // Process children using fullPath as parent context
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => processRoute(child, fullPath))
    }
  }

  // Process all user routes
  userRoutes.forEach((route) => processRoute(route))

  // Always ensure dashboard route exists (even if user_routes is empty or doesn't include it)
  const hasDashboard = routes.some((r) => r.path === '/dashboard')
  if (!hasDashboard) {
    const DashboardComponent = getRouteComponent('/dashboard')
    if (DashboardComponent) {
      routes.push({
        path: '/dashboard',
        element: wrapProtected(<DashboardComponent />, '/dashboard'),
      })
    }
  }

  // Always ensure parent ticket routes exist (may not be in user_routes from backend)
  const ticketPaths = ['/tickets', '/tickets/create', '/tickets/:id/edit', '/tickets/:id/view']
  for (const path of ticketPaths) {
    if (!routes.some((r) => r.path === path)) {
      const Component = getRouteComponent(path)
      if (Component) {
        routes.push({
          path,
          element: wrapProtected(<Component />, path),
        })
      }
    }
  }

  // Always ensure agent status route exists (linked from header / sidebar)
  const agentStatusPath = '/agent-status'
  if (!routes.some((r) => r.path === agentStatusPath)) {
    const AgentStatusComponent = getRouteComponent(agentStatusPath)
    if (AgentStatusComponent) {
      routes.push({
        path: agentStatusPath,
        element: wrapProtected(<AgentStatusComponent />, agentStatusPath),
      })
    }
  }

  // Fallback to dashboard for any unmatched route (only after initialization)
  const includeFallback = options.includeFallback ?? true
  if (includeFallback && routes.length > publicRoutes.length + 1) {
    routes.push({
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    })
  }

  

  return routes
}
